/**
 * Apply policy for AppSync `messageType: config` card events.
 * The socket body is a hint; hydrate via `getItem.card` when this returns `hydrate`.
 */

export type CardConfigRealtimeIgnoreReason =
	| 'not-card-entity'
	| 'missing-card-id'
	| 'own-actor'
	| 'pending'
	| 'stale'
	| 'not-on-layout'
	| 'locally-deleted';

export type CardConfigRealtimeDecision =
	| { action: 'ignore'; reason: CardConfigRealtimeIgnoreReason }
	| { action: 'delete'; reason: 'deleted' }
	| { action: 'hydrate'; reason: 'update' | 'create' };

export interface CardConfigRealtimeContext {
	/** JWT userKey, JWT userId, Cognito username/sub, and WS requester. */
	currentActorIds: ReadonlyArray<string | null | undefined>;
	localRevisions: number | null | undefined;
	hasPendingChanges: boolean;
	cardInState: boolean;
	cardOnLayout: boolean;
	locallyDeleted?: boolean;
	/**
	 * The card is in local state but has no server revision yet (an agent-placed loading
	 * shell, a minimal card from the page list, or a legacy card). The agent writes as the
	 * signed-in user, so its events look like the user's own edits; such a card has no
	 * saved edits of the user's to protect and must still be filled in from the server.
	 */
	hasNoServerRevision?: boolean;
}

interface CardLayoutRow {
	cardId?: string;
}

interface LayoutLike {
	pageId?: string;
	cards?: CardLayoutRow[];
}

interface PageLike {
	pageId?: string;
	layout?: CardLayoutRow[];
}

function trimId(value: unknown): string {
	return typeof value === 'string' ? value.trim() : '';
}

function isCardEntity(entityType: unknown): boolean {
	const type = trimId(entityType).toLowerCase();
	return type === '' || type === 'card';
}

function isDeleteEvent(event: Record<string, unknown>): boolean {
	if (event['isDeleted'] === true) {
		return true;
	}
	return trimId(event['eventAction']).toLowerCase() === 'delete';
}

function isCreateEvent(event: Record<string, unknown>): boolean {
	return trimId(event['eventAction']).toLowerCase() === 'create';
}

function eventActorIds(event: Record<string, unknown>): string[] {
	return [event['userId'], event['requester']]
		.map(trimId)
		.filter(id => id.length > 0);
}

function isOwnActor(
	event: Record<string, unknown>,
	currentActorIds: ReadonlyArray<string | null | undefined>
): boolean {
	const locals = new Set(
		currentActorIds.map(trimId).filter(id => id.length > 0)
	);
	if (locals.size === 0) {
		return false;
	}
	return eventActorIds(event).some(id => locals.has(id));
}

function isStaleRevision(
	event: Record<string, unknown>,
	localRevisions: number | null | undefined
): boolean {
	const incoming = event['revisions'];
	if (typeof incoming !== 'number' || !Number.isFinite(incoming)) {
		return false;
	}
	if (typeof localRevisions !== 'number' || !Number.isFinite(localRevisions)) {
		return false;
	}
	if (isDeleteEvent(event)) {
		return incoming < localRevisions;
	}
	return incoming <= localRevisions;
}

export function decideCardConfigRealtimeApply(
	event: Record<string, unknown>,
	ctx: CardConfigRealtimeContext
): CardConfigRealtimeDecision {
	if (!isCardEntity(event['entityType'])) {
		return { action: 'ignore', reason: 'not-card-entity' };
	}

	const cardId = trimId(event['cardId']);
	if (!cardId) {
		return { action: 'ignore', reason: 'missing-card-id' };
	}

	if (ctx.locallyDeleted) {
		return { action: 'ignore', reason: 'locally-deleted' };
	}

	const hydratesShell =
		ctx.hasNoServerRevision === true && !isDeleteEvent(event);
	if (!hydratesShell && isOwnActor(event, ctx.currentActorIds)) {
		return { action: 'ignore', reason: 'own-actor' };
	}

	if (isStaleRevision(event, ctx.localRevisions)) {
		return { action: 'ignore', reason: 'stale' };
	}

	if (isDeleteEvent(event)) {
		return { action: 'delete', reason: 'deleted' };
	}

	if (ctx.hasPendingChanges) {
		return { action: 'ignore', reason: 'pending' };
	}

	const knownLocally = ctx.cardInState || ctx.cardOnLayout;
	if (!knownLocally) {
		return { action: 'ignore', reason: 'not-on-layout' };
	}

	if (isCreateEvent(event)) {
		return { action: 'hydrate', reason: 'create' };
	}

	return { action: 'hydrate', reason: 'update' };
}

/**
 * Html/markdown `content` is runtime text with its own requestId guard.
 * Apply it even when hydrate is skipped for `not-on-layout`.
 */
export function shouldApplyCardConfigRealtimeContent(
	decision: CardConfigRealtimeDecision,
	event: Record<string, unknown>
): boolean {
	const cardId = trimId(event['cardId']);
	const cardType = trimId(event['cardType']).toLowerCase();
	if (!cardId || typeof event['content'] !== 'string') {
		return false;
	}
	if (cardType !== 'html' && cardType !== 'markdown') {
		return false;
	}
	if (decision.action === 'delete') {
		return false;
	}
	if (decision.action === 'ignore') {
		return decision.reason === 'not-on-layout';
	}
	return true;
}

export function resolveCardLayoutPageId(
	layouts: Iterable<LayoutLike>,
	pages: Iterable<PageLike>,
	cardId: string
): string | null {
	const normalized = trimId(cardId);
	if (!normalized) {
		return null;
	}
	for (const layout of layouts) {
		if (layout.cards?.some(card => trimId(card.cardId) === normalized)) {
			const pageId = trimId(layout.pageId);
			if (pageId) {
				return pageId;
			}
		}
	}
	for (const page of pages) {
		if (page.layout?.some(card => trimId(card.cardId) === normalized)) {
			const pageId = trimId(page.pageId);
			if (pageId) {
				return pageId;
			}
		}
	}
	return null;
}
